/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Lobby domain - Lobby browser UI system for lobby discovery, auto-join, and lobby card management
 */

import type { LobbyMetadata } from "@game/domain";
import { startThemeMusic } from "../audio/audio";
import { loadoutService } from "../loadout/loadout.service";
import { createLogger } from "../logger";
import {
  getActiveRealtimeBackend,
  isActiveRealtimeBackendConnected,
} from "../networking/realtime.runtime";
import { isJoinModalVisible } from "../ui/modals";
import { throttleNetworkRequest } from "../ui/performance";
import { showToast } from "../ui/toasts";

const logger = createLogger("lobby");

let lobbiesList: LobbyMetadata[] = [];
const LOBBY_LIST_REFRESH_INTERVAL = 2000;

let isAutoJoinEnabled = false;
let hasAttemptedAutoJoin = false;

// Connection toast debouncing - prevent "Connection not available" on initial load
let firstConnectionEstablishedAt = 0;
let lastDisconnectDetectedAt = 0;
const MIN_CONNECTED_TIME_MS = 3000;
const MIN_DISCONNECT_TIME_MS = 2000;

// Toast debouncing - prevent repeated error toasts
const lastToastTimes = new Map<string, number>();
const TOAST_DEBOUNCE_MS = 5000;

let quickJoinBtnEl: HTMLButtonElement;
let refreshLobbiesBtnEl: HTMLButtonElement;
let lobbyListEl: HTMLElement;

let dependencies: {
  sendMessage: (msg: any) => void;
  state: any;
  getPlayerNameInput: () => string;
} | null = null;

export function setBrowserDependencies(deps: {
  sendMessage: (msg: any) => void;
  state: any;
  getPlayerNameInput: () => string;
}) {
  dependencies = deps;
}

export function initLobbyBrowserUI() {
  quickJoinBtnEl = document.getElementById("quickJoinBtn") as HTMLButtonElement;
  refreshLobbiesBtnEl = document.getElementById(
    "refreshLobbiesBtn",
  ) as HTMLButtonElement;
  lobbyListEl = document.getElementById("lobbyList") as HTMLElement;

  if (quickJoinBtnEl) {
    quickJoinBtnEl.onclick = handleQuickJoin;
  }

  if (refreshLobbiesBtnEl) {
    refreshLobbiesBtnEl.onclick = () => throttledRequestLobbyList();
  }

  const autoJoinToggle = document.getElementById(
    "autoJoinToggle",
  ) as HTMLInputElement;
  if (autoJoinToggle) {
    const savedAutoJoin = localStorage.getItem("autoJoinEnabled");
    isAutoJoinEnabled = savedAutoJoin === "true";
    autoJoinToggle.checked = isAutoJoinEnabled;

    autoJoinToggle.addEventListener("change", (e) => {
      isAutoJoinEnabled = (e.target as HTMLInputElement).checked;
      localStorage.setItem("autoJoinEnabled", isAutoJoinEnabled.toString());

      if (
        isAutoJoinEnabled &&
        dependencies?.state &&
        !dependencies.state.started &&
        isJoinModalVisible()
      ) {
        checkAndAutoJoin();
      }
    });
  }

  setInterval(() => {
    if (dependencies?.state?.connectionStatus === "ONLINE") {
      throttledRequestLobbyList();
    }
  }, LOBBY_LIST_REFRESH_INTERVAL);
}

export function trackConnectionState(isConnected: boolean) {
  if (isConnected) {
    if (firstConnectionEstablishedAt === 0) {
      firstConnectionEstablishedAt = Date.now();
    }
  } else {
    if (lastDisconnectDetectedAt === 0) {
      lastDisconnectDetectedAt = Date.now();
    }
  }
}

function shouldShowDisconnectionToast(): boolean {
  const now = Date.now();

  if (firstConnectionEstablishedAt === 0) {
    return false;
  }

  const timeConnected = now - firstConnectionEstablishedAt;
  if (timeConnected < MIN_CONNECTED_TIME_MS) {
    return false;
  }

  // First disconnect detection - mark timestamp and don't show yet
  if (lastDisconnectDetectedAt === 0) {
    lastDisconnectDetectedAt = now;
    return false;
  }

  const timeDisconnected = now - lastDisconnectDetectedAt;
  return timeDisconnected >= MIN_DISCONNECT_TIME_MS;
}

export function resetDisconnectTracking() {
  lastDisconnectDetectedAt = 0;
}

function showDebouncedToast(
  message: string,
  type: "info" | "warning" | "error",
) {
  const now = Date.now();
  const lastShown = lastToastTimes.get(message) || 0;

  if (now - lastShown >= TOAST_DEBOUNCE_MS) {
    showToast(message, type);
    lastToastTimes.set(message, now);
  }
}

export function requestLobbyList(): boolean {
  try {
    if (!dependencies) {
      logger.warn("[LOBBY_BROWSER] Dependencies not initialized");
      return false;
    }

    const activeRealtimeBackend = getActiveRealtimeBackend();
    if (!activeRealtimeBackend || !isActiveRealtimeBackendConnected()) {
      if (shouldShowDisconnectionToast()) {
        showDebouncedToast("Connection not available", "error");
      }
      return false;
    }

    dependencies.sendMessage({ t: "lobby_list_request" });
    return true;
  } catch (_error) {
    showDebouncedToast("Failed to request lobby list", "error");
    return false;
  }
}

const throttledRequestLobbyList = throttleNetworkRequest(requestLobbyList);

export function handleQuickJoin(): void {
  const firstLobby = lobbiesList.find(
    (lobby) =>
      lobby.status !== "in_progress" &&
      lobby.status !== "finished" &&
      lobby.playerCount < lobby.maxPlayers,
  );

  if (firstLobby) {
    joinSpecificLobby(firstLobby.id);
  } else {
    showToast("No available lobbies to join", "warning");
  }
}

export function checkAndAutoJoin() {
  if (!dependencies) return;

  if (
    !isAutoJoinEnabled ||
    dependencies.state.started ||
    hasAttemptedAutoJoin
  ) {
    return;
  }

  if (!isJoinModalVisible()) {
    return;
  }

  const playerName = dependencies.getPlayerNameInput()?.trim();
  if (!playerName) {
    return;
  }

  const firstLobby = lobbiesList[0];
  if (
    firstLobby &&
    firstLobby.status !== "in_progress" &&
    firstLobby.status !== "finished"
  ) {
    hasAttemptedAutoJoin = true;

    localStorage.setItem("playerName", playerName);

    const success = joinSpecificLobby(firstLobby.id);

    // Reset the flag if join failed so we can try again
    if (!success) {
      setTimeout(() => {
        hasAttemptedAutoJoin = false;
      }, 3000);
    }
  }
}

export function joinSpecificLobby(lobbyId: string): boolean {
  try {
    if (!dependencies) {
      logger.warn("[LOBBY_BROWSER] Dependencies not initialized");
      return false;
    }

    if (!lobbyId || typeof lobbyId !== "string") {
      showToast("Invalid lobby ID", "error");
      return false;
    }

    const activeRealtimeBackend = getActiveRealtimeBackend();
    if (!activeRealtimeBackend || !isActiveRealtimeBackendConnected()) {
      logger.warn(
        "[LOBBY_CLIENT] Cannot join lobby: realtime backend not ready",
      );
      showDebouncedToast("Reconnecting...", "warning");
      return false;
    }

    if (dependencies.state.lobbyId) {
      showToast("Already in a lobby", "warning");
      return false;
    }

    const targetLobby = lobbiesList.find((l) => l.id === lobbyId);
    if (!targetLobby) {
      showToast("Lobby no longer available", "warning");
      requestLobbyList();
      return false;
    }

    if (
      targetLobby.status === "in_progress" ||
      targetLobby.status === "finished"
    ) {
      showToast("Game has already started", "warning");
      return false;
    }

    if (targetLobby.playerCount >= targetLobby.maxPlayers) {
      showToast("Lobby is full", "warning");
      return false;
    }

    const playerName = dependencies.getPlayerNameInput();

    const party = dependencies.state.party;
    if (party && party.members.length > 1) {
      const allReady = party.members.every((m: any) => m.ready);

      if (!allReady) {
        showToast("All squadron members must be ready", "warning");
        return false;
      }

      dependencies.sendMessage({ t: "party_queue" });
      return true;
    }

    const selectedColor = "default";

    const weapon = loadoutService.getCurrentWeapon();
    logger.info(`[LOBBY_JOIN] Sending join request with weapon: ${weapon}`);

    dependencies.sendMessage({
      t: "lobby_join_request",
      lobbyId,
      name: playerName || undefined,
      color: selectedColor,
      primaryWeapon: weapon,
    });

    startThemeMusic();

    showToast("Joining lobby...", "info");
    return true;
  } catch (_error) {
    showDebouncedToast("Failed to join lobby", "error");
    return false;
  }
}

export function updateLobbyList(lobbies: LobbyMetadata[]): boolean {
  try {
    if (!lobbies || !Array.isArray(lobbies)) {
      showDebouncedToast("Invalid lobby data received", "error");
      return false;
    }

    if (window.lobbyBridge) {
      window.lobbyBridge.updateLobbies(lobbies);

      lobbiesList = lobbies;
      checkAndAutoJoin();
      return true;
    }

    lobbiesList = lobbies;

    if (!lobbyListEl) {
      return false;
    }

    if (lobbies.length === 0) {
      lobbyListEl.innerHTML = `
        <div class="lobby-list-empty">
          <span>No active lobbies found</span>
          <small>Click REFRESH to check for lobbies</small>
        </div>
      `;
      return true;
    }

    lobbyListEl.innerHTML = lobbies
      .slice(0, 3)
      .map(
        (lobby) => `
        <div class="lobby-card" data-lobby-id="${lobby.id}">
          <div>Lobby ${lobby.id.substring(0, 6).toUpperCase()}</div>
          <div>${lobby.playerCount}/${lobby.maxPlayers} players</div>
          <div>${lobby.secondsUntilStart}s until start</div>
        </div>
      `,
      )
      .join("");

    checkAndAutoJoin();

    return true;
  } catch (_error) {
    showDebouncedToast("Failed to update lobby list", "error");
    return false;
  }
}

export function getLobbiesList(): LobbyMetadata[] {
  return [...lobbiesList];
}

export function getAutoJoinEnabled(): boolean {
  return isAutoJoinEnabled;
}

export function resetAutoJoinAttempt(): void {
  hasAttemptedAutoJoin = false;
}

export function getBrowserStats() {
  return {
    lobbiesCount: lobbiesList.length,
    autoJoinEnabled: isAutoJoinEnabled,
    hasAttemptedAutoJoin,
    refreshInterval: LOBBY_LIST_REFRESH_INTERVAL,
  };
}

export function resetBrowserState() {
  lobbiesList = [];
  isAutoJoinEnabled = false;
  hasAttemptedAutoJoin = false;
  firstConnectionEstablishedAt = 0;
  lastDisconnectDetectedAt = 0;
  lastToastTimes.clear();
  dependencies = null;
  // Reset DOM element references - will be re-initialized by initLobbyBrowserUI
  quickJoinBtnEl = null as any;
  refreshLobbiesBtnEl = null as any;
  lobbyListEl = null as any;
}
